"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getDraftPurgeSettingAction,
  saveDraftPurgeSettingAction,
} from "@/app/lib/actions/cat/draft-purge-setting";
import { runDraftPurgeManuallyAction } from "@/app/lib/actions/cat/draft-purge-manual";
import { AbsoluteDate } from "@/components/utils/date-utils";

function AdministrationDraftPurge() {
  const [ttlDays, setTtlDays] = useState(7);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [lastPurgeResult, setLastPurgeResult] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const result = await getDraftPurgeSettingAction();
      if (result.success && result.data) {
        setTtlDays(result.data.ttlDays);
        setUpdatedAt(
          result.data.updatedAt ? new Date(result.data.updatedAt) : null,
        );
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const result = await saveDraftPurgeSettingAction({ ttlDays });
    setSaving(false);

    if (!result.success) {
      toast.error(result.message ?? "Impossible d'enregistrer le TTL.");
      return;
    }

    toast.success("Durée de vie des brouillons enregistrée.");
    setUpdatedAt(new Date());
  };

  const handlePurge = async () => {
    setPurging(true);
    const result = await runDraftPurgeManuallyAction();
    setPurging(false);

    if (!result.success) {
      toast.error(result.message ?? "La purge a échoué.");
      setLastPurgeResult(result.message ?? "Erreur lors de la purge.");
      return;
    }

    const deletedCount = result.data?.deletedCount ?? 0;
    const message = `${deletedCount} brouillon(s) supprimé(s).`;
    toast.success(message);
    setLastPurgeResult(`${message} (${new Date().toLocaleString("fr-FR")})`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Purge des brouillons CAT</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="draft-ttl-days">
              Durée de vie (TTL, en jours)
            </FieldLabel>
            <Input
              id="draft-ttl-days"
              type="number"
              min={1}
              max={365}
              value={ttlDays}
              disabled={loading}
              onChange={(event) => setTtlDays(Number(event.target.value))}
            />
          </Field>
        </FieldGroup>

        {updatedAt ? (
          <p className="text-muted-foreground text-sm">
            Dernière modification du TTL : <AbsoluteDate date={updatedAt} />
          </p>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <Button
            disabled={loading || saving}
            onClick={() => void handleSave()}
          >
            Enregistrer
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={loading || purging}>
                Exécuter la purge maintenant
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Supprimer définitivement tous les brouillons expirés ?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action supprime tous les brouillons dont la dernière
                  modification dépasse le TTL configuré ({ttlDays} jour
                  {ttlDays > 1 ? "s" : ""}). Elle est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handlePurge()}>
                  Confirmer la purge
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {lastPurgeResult ? (
          <p className="text-sm">Dernier résultat : {lastPurgeResult}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export { AdministrationDraftPurge };
